// apps/client/src/pages/leads/website/websiteConnectionForm.ts
//
// Pure helpers for the Website tab: form state <-> the callable input, origin
// validation, the install snippet and display labels. No React, so every
// rule here is unit tested directly.
//
// Origins are validated with the SAME function the server uses
// (normaliseAllowedOrigin from shared-schemas), so the form can never accept
// an origin the callable then refuses, or the reverse.

import {
  DEFAULT_PROTECTION_MODE,
  ENQUIRY_VARIANT_KEY_PATTERN,
  MAX_ALLOWED_ORIGINS,
  MAX_ENQUIRY_VARIANTS,
  isValuttenOrigin,
  normaliseAllowedOrigin,
  type AllowedOriginRejection,
  type EnquiryVariant,
  type EnquiryVariantPreset,
  type ProtectionMode,
  type WebsiteConnectionInput,
  type WebsiteFormKind,
} from '@valutten/shared-schemas/dist/websiteConnection';
import {
  emptyClassification,
  resolveClassificationOptions,
  validateClassification,
} from '@valutten/shared-schemas/dist/leadClassification';
import type { ClassificationOptions, LeadClassification } from '@valutten/shared-schemas';
import type { WebsiteConnectionView } from '../../../hooks/useWebsiteConnections';

// ─── Form state ─────────────────────────────────────────────────────────────

/**
 * One named enquiry form (connection forms.enquiry.variants[]). The website
 * picks it by sending `variant` = key. Preset answers are held as a lead
 * classification so the manual lead form's fields edit them.
 */
export interface EnquiryVariantFormState {
  key: string;
  label: string;
  /** '' = not fixed. */
  serviceCategory: string;
  /** '' = company-wide. */
  officeId: string;
  preset: LeadClassification;
}

export interface WebsiteConnectionFormState {
  label: string;
  environment: 'production' | 'staging';
  /** One origin per line, as typed. */
  originsText: string;
  mode: ProtectionMode;
  /** 0 to 1, step 0.1. */
  minScore: number;
  enquiryEnabled: boolean;
  /** '' = no fixed service category (the lead's own answers decide). */
  enquiryServiceCategory: string;
  /** '' = company-wide (routing decides the office). */
  enquiryOfficeId: string;
  /** Named enquiry forms, in display order. */
  enquiryVariants: EnquiryVariantFormState[];
  /**
   * The stored connection carried a `variants` key. Only then (or when the
   * list is non-empty) is `variants` sent, so a connection that never had
   * variants saves exactly as before, and removing the last one still
   * clears it.
   */
  hadEnquiryVariants: boolean;
  newsletterEnabled: boolean;
  welcomeEmail: boolean;
  referralEnabled: boolean;
  referralPartnerId: string;
  /** Each website referral also becomes a routed lead. */
  referralRouteAsLead: boolean;
  /** '' = not fixed. */
  referralServiceCategory: string;
  /** '' = company-wide. */
  referralOfficeId: string;
  /** The stored referral form carried routing keys (same rule as hadEnquiryVariants). */
  hadReferralRouting: boolean;
  /** Daily synthetic test submission (connection canary.enabled). Edited on update only. */
  canaryEnabled: boolean;
}

export function emptyFormState(): WebsiteConnectionFormState {
  return {
    label: '',
    environment: 'production',
    originsText: '',
    mode: DEFAULT_PROTECTION_MODE,
    minScore: 0.5,
    enquiryEnabled: true,
    enquiryServiceCategory: '',
    enquiryOfficeId: '',
    enquiryVariants: [],
    hadEnquiryVariants: false,
    newsletterEnabled: false,
    welcomeEmail: true,
    referralEnabled: false,
    referralPartnerId: '',
    referralRouteAsLead: false,
    referralServiceCategory: '',
    referralOfficeId: '',
    hadReferralRouting: false,
    canaryEnabled: false,
  };
}

function classificationFromPreset(p: EnquiryVariantPreset | null | undefined): LeadClassification {
  return {
    loanPurpose: p?.loanPurpose || null,
    propertyUse: p?.propertyUse || null,
    borrowerSituations: (p?.borrowerSituations ?? []).filter((x): x is string => typeof x === 'string' && x.length > 0),
    loanStructure: p?.loanStructure || null,
    loanAmount: p?.loanAmount || null,
  };
}

/** The preset to store: answered dimensions only, so an empty preset is {}. */
export function presetFromClassification(c: LeadClassification): EnquiryVariantPreset {
  const out: EnquiryVariantPreset = {};
  if (c.loanPurpose) out.loanPurpose = c.loanPurpose;
  if (c.propertyUse) out.propertyUse = c.propertyUse;
  if (c.borrowerSituations.length > 0) out.borrowerSituations = [...c.borrowerSituations];
  if (c.loanStructure) out.loanStructure = c.loanStructure;
  if (c.loanAmount) out.loanAmount = c.loanAmount;
  return out;
}

export function variantFormStateFrom(v: EnquiryVariant): EnquiryVariantFormState {
  return {
    key: v.key ?? '',
    label: v.label ?? '',
    serviceCategory: v.serviceCategory ?? '',
    officeId: v.officeId ?? '',
    preset: classificationFromPreset(v.preset),
  };
}

export function formStateFromConnection(c: WebsiteConnectionView): WebsiteConnectionFormState {
  const enquiry = c.forms?.enquiry;
  const referral = c.forms?.referral;
  return {
    label: c.label ?? '',
    environment: c.environment === 'staging' ? 'staging' : 'production',
    originsText: (c.allowedOrigins ?? []).join('\n'),
    mode: c.mode ?? DEFAULT_PROTECTION_MODE,
    minScore: typeof c.bot?.minScore === 'number' ? c.bot.minScore : 0.5,
    enquiryEnabled: c.forms?.enquiry?.enabled === true,
    enquiryServiceCategory: c.forms?.enquiry?.serviceCategory ?? '',
    enquiryOfficeId: c.forms?.enquiry?.officeId ?? '',
    enquiryVariants: Array.isArray(enquiry?.variants) ? enquiry.variants.map(variantFormStateFrom) : [],
    hadEnquiryVariants: Array.isArray(enquiry?.variants),
    newsletterEnabled: c.forms?.newsletter?.enabled === true,
    welcomeEmail: c.forms?.newsletter?.welcomeEmail !== false,
    referralEnabled: c.forms?.referral?.enabled === true,
    referralPartnerId: c.forms?.referral?.partnerId ?? '',
    referralRouteAsLead: referral?.routeAsLead === true,
    referralServiceCategory: referral?.serviceCategory ?? '',
    referralOfficeId: referral?.officeId ?? '',
    hadReferralRouting:
      !!referral && (referral.routeAsLead !== undefined || referral.serviceCategory !== undefined || referral.officeId !== undefined),
    canaryEnabled: c.canary?.enabled === true,
  };
}

// ─── Validation ─────────────────────────────────────────────────────────────

const ORIGIN_REJECTION_MESSAGES: Readonly<Record<AllowedOriginRejection, string>> = {
  empty: 'is empty',
  not_url: 'is not a web address',
  not_https: 'must start with https://',
  wildcard: 'cannot use a wildcard (*). List each site separately',
  has_path: 'must be the site address only, with no page path, query or #',
  has_credentials: 'cannot include a username or password',
};

export interface WebsiteConnectionFormErrors {
  label?: string;
  origins?: string[];
  referralPartner?: string;
  forms?: string;
  /** Per variant index: what is wrong with it. */
  variants?: Record<number, string>;
}

export type FormValidation =
  | { ok: true; input: WebsiteConnectionInput; origins: string[] }
  | { ok: false; errors: WebsiteConnectionFormErrors };

/**
 * Parse the typed origins: one per line (commas and spaces also split),
 * blanks ignored, each normalised to the exact origin the browser sends.
 */
export function parseOrigins(text: string): { origins: string[]; errors: string[] } {
  const entries = (text ?? '')
    .split(/[\n,\s]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  const origins: string[] = [];
  const errors: string[] = [];
  for (const entry of entries) {
    const r = normaliseAllowedOrigin(entry);
    if (!r.ok) {
      errors.push(`"${entry}" ${ORIGIN_REJECTION_MESSAGES[r.reason]}.`);
      continue;
    }
    if (isValuttenOrigin(r.origin)) {
      errors.push(`"${entry}" is a VALUTTEN address and cannot be added here.`);
      continue;
    }
    if (!origins.includes(r.origin)) origins.push(r.origin);
  }
  if (entries.length === 0) errors.push('Add at least one website address, for example https://www.example.com.au');
  if (origins.length > MAX_ALLOWED_ORIGINS) errors.push(`Up to ${MAX_ALLOWED_ORIGINS} addresses per connection.`);
  return { origins, errors };
}

export function validateForm(state: WebsiteConnectionFormState): FormValidation {
  const errors: WebsiteConnectionFormErrors = {};
  const label = state.label.trim();
  if (label.length === 0) errors.label = 'Give the connection a name, for example "Main website".';
  else if (label.length > 80) errors.label = 'Keep the name to 80 characters or fewer.';

  const { origins, errors: originErrors } = parseOrigins(state.originsText);
  if (originErrors.length > 0) errors.origins = originErrors;

  if (state.referralEnabled && !state.referralPartnerId) {
    errors.referralPartner = 'Choose the partner that receives website referrals, or turn the referral form off.';
  }
  if (!state.enquiryEnabled && !state.newsletterEnabled && !state.referralEnabled) {
    errors.forms = 'Turn on at least one form.';
  }

  const variantErrors = validateVariants(state.enquiryVariants);
  if (Object.keys(variantErrors).length > 0) errors.variants = variantErrors;

  if (Object.keys(errors).length > 0) return { ok: false, errors };

  const minScore = Math.min(1, Math.max(0, Math.round(state.minScore * 10) / 10));
  return {
    ok: true,
    origins,
    input: {
      label,
      environment: state.environment,
      allowedOrigins: origins,
      mode: state.mode,
      bot: { minScore },
      forms: {
        enquiry: {
          enabled: state.enquiryEnabled,
          serviceCategory: state.enquiryServiceCategory || null,
          officeId: state.enquiryOfficeId || null,
          ...(state.hadEnquiryVariants || state.enquiryVariants.length > 0
            ? { variants: state.enquiryVariants.map(variantInput) }
            : {}),
        },
        newsletter: { enabled: state.newsletterEnabled, welcomeEmail: state.welcomeEmail },
        referral: {
          enabled: state.referralEnabled,
          partnerId: state.referralPartnerId || null,
          ...(state.hadReferralRouting || state.referralRouteAsLead
            ? {
              routeAsLead: state.referralRouteAsLead,
              serviceCategory: state.referralServiceCategory || null,
              officeId: state.referralOfficeId || null,
            }
            : {}),
        },
      },
    },
  };
}

function variantInput(v: EnquiryVariantFormState): EnquiryVariant {
  return {
    key: v.key.trim(),
    label: v.label.trim(),
    serviceCategory: v.serviceCategory || null,
    officeId: v.officeId || null,
    preset: presetFromClassification(v.preset),
  };
}

/** Index -> message for each variant that cannot be saved. */
export function validateVariants(variants: readonly EnquiryVariantFormState[]): Record<number, string> {
  const out: Record<number, string> = {};
  const seen = new Set<string>();
  variants.forEach((v, i) => {
    const key = v.key.trim();
    const label = v.label.trim();
    if (i >= MAX_ENQUIRY_VARIANTS) out[i] = `Up to ${MAX_ENQUIRY_VARIANTS} named forms per connection. Remove this one.`;
    else if (!label) out[i] = 'Give this form a name.';
    else if (label.length > 80) out[i] = 'Keep the name to 80 characters or fewer.';
    else if (!ENQUIRY_VARIANT_KEY_PATTERN.test(key)) {
      out[i] = 'The form key must start with a lowercase letter and use only lowercase letters, digits, - or _ (up to 40 characters).';
    } else if (seen.has(key)) out[i] = `Another form already uses the key "${key}". Each key must be different.`;
    seen.add(key);
  });
  return out;
}

// ─── Enquiry form presets ───────────────────────────────────────────────────

export type EnquiryPresetId = 'home_loan' | 'refinance' | 'off_the_plan' | 'commercial' | 'asset_finance';

interface EnquiryPresetDef {
  id: EnquiryPresetId;
  label: string;
  /** Tenant service categories tried in order, matched on id or label. */
  service: RegExp[];
  answers: Partial<Pick<LeadClassification, 'loanPurpose' | 'propertyUse' | 'loanStructure' | 'loanAmount'>> & {
    borrowerSituations?: string[];
  };
}

const HOME_SERVICE = [/home|mortgage|residential/i, /^finance$/i, /financ/i];

/** Answer ids are VALUTTEN's default option ids; ones the service does not offer are dropped. */
export const ENQUIRY_PRESETS: readonly EnquiryPresetDef[] = [
  { id: 'home_loan', label: 'Home loan', service: HOME_SERVICE, answers: { loanStructure: 'standard_residential' } },
  {
    id: 'refinance',
    label: 'Refinance',
    service: HOME_SERVICE,
    answers: { loanPurpose: 'refinance', loanStructure: 'standard_residential' },
  },
  {
    id: 'off_the_plan',
    label: 'Off the plan',
    service: HOME_SERVICE,
    answers: { loanPurpose: 'purchase', loanStructure: 'standard_residential' },
  },
  { id: 'commercial', label: 'Commercial', service: [/commercial/i, ...HOME_SERVICE], answers: { loanStructure: 'commercial' } },
  { id: 'asset_finance', label: 'Asset finance', service: [/asset/i, /equipment|vehicle/i], answers: {} },
];

/** Lowercase slug that satisfies ENQUIRY_VARIANT_KEY_PATTERN, unique among `taken`. */
export function variantKeyFrom(text: string, taken: readonly string[]): string {
  let base = text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^[^a-z]+/, '')
    .replace(/_+$/, '')
    .slice(0, 36);
  if (!base) base = 'form';
  let key = base;
  for (let n = 2; taken.includes(key); n += 1) key = `${base}_${n}`;
  return key;
}

/**
 * A new variant from a preset: label, a unique key, the tenant's own matching
 * service category (none when nothing matches), and only the preset answers
 * that service actually offers, so nothing is saved that the submit would drop.
 */
export function variantFromPreset(
  presetId: EnquiryPresetId,
  ctx: {
    serviceCategories: ReadonlyArray<{ id: string; label: string }>;
    classification: Record<string, ClassificationOptions> | null | undefined;
    takenKeys: readonly string[];
  },
): EnquiryVariantFormState {
  const def = ENQUIRY_PRESETS.find((p) => p.id === presetId) ?? ENQUIRY_PRESETS[0];
  let serviceCategory = '';
  for (const re of def.service) {
    const hit = ctx.serviceCategories.find((c) => re.test(c.id) || re.test(c.label));
    if (hit) {
      serviceCategory = hit.id;
      break;
    }
  }
  const options = resolveClassificationOptions(ctx.classification ?? undefined, serviceCategory || null);
  const preset = options ? validateClassification(def.answers, options).value : emptyClassification();
  return {
    key: variantKeyFrom(def.id, ctx.takenKeys),
    label: def.label,
    serviceCategory,
    officeId: '',
    preset,
  };
}

/** A blank named form with a unique key. */
export function blankVariant(takenKeys: readonly string[]): EnquiryVariantFormState {
  return { key: variantKeyFrom('form', takenKeys), label: '', serviceCategory: '', officeId: '', preset: emptyClassification() };
}

// ─── Install snippet ────────────────────────────────────────────────────────

/**
 * Public API base for the website SDK in this environment, or null where no
 * public endpoint exists (DEV is dormant and has no edge).
 */
export function websiteFormsApiBase(projectId: string | null | undefined): string | null {
  if (projectId === 'valutten-prod') return 'https://api.valutten.com';
  if (projectId === 'valutten-stage') return 'https://api-stage.valutten.com';
  return null;
}

/** The two script tags a website pastes before </body>. */
export function buildInstallSnippet(siteKey: string, apiBase: string): string {
  return [
    `<script src="${apiBase}/forms/v1/sdk.js" defer></script>`,
    '<script>',
    "  window.addEventListener('DOMContentLoaded', function () {",
    `    window.ValuttenForms.init({ siteKey: '${siteKey}', apiBase: '${apiBase}' });`,
    '  });',
    '</script>',
  ].join('\n');
}

// ─── Labels ─────────────────────────────────────────────────────────────────

export const FORM_LABELS: Readonly<Record<WebsiteFormKind, string>> = {
  enquiry: 'Enquiry',
  newsletter: 'Newsletter',
  referral: 'Referral',
};

export const MODE_LABELS: Readonly<Record<ProtectionMode, { label: string; help: string }>> = {
  off: { label: 'Off', help: 'No origin or bot checks. Use only while testing.' },
  observe: {
    label: 'Observe',
    help: 'Checks run and are recorded in Health, but nothing is refused. Start here.',
  },
  enforce: {
    label: 'Enforce',
    help: 'Submissions from other sites, or that fail the bot check, are refused.',
  },
};

/**
 * Labels for the rejection reason keys the submit pipeline records
 * (services/client-functions/src/website/submit.ts `reject(...)` and
 * recordWebsiteStats, plus every BotViolation in website/recaptcha.ts).
 * websiteConnectionForm.test.ts pins each key against that source.
 */
export const REJECTION_LABELS: Readonly<Record<string, string>> = {
  form_disabled: 'Form turned off',
  origin_missing: 'No website address sent',
  origin_not_allowed: 'Sent from a website not on the list',
  bot_token_missing: 'Bot check not completed',
  bot_token_invalid: 'Bot check token invalid',
  bot_action_mismatch: 'Bot check for a different form',
  bot_hostname_mismatch: 'Bot check from a different website',
  bot_low_score: 'Bot score too low',
  invalid_data: 'Missing or invalid details',
  consent_missing: 'Required consent not given',
  consent_version_invalid: 'Out-of-date consent wording',
  address_throttled: 'Repeat submissions from one email address',
  mailbox_throttled: 'Repeat submissions from one mailbox',
  failed_consent: 'Not saved: consent could not be recorded',
  failed_handler: 'Not saved: processing failed',
};

/** `rate_limit_<scope>` (website/abuse.ts caps) -> label. */
const RATE_LIMIT_SCOPE_LABELS: Readonly<Record<string, string>> = {
  ip: 'Too many submissions from one device',
  company: 'Too many submissions for your company this hour',
  unavailable: 'Rate limit check unavailable',
};

/** Plain-English label for a rejection reason key; unknown keys are humanised. */
export function rejectionLabel(reason: string): string {
  if (Object.prototype.hasOwnProperty.call(REJECTION_LABELS, reason)) return REJECTION_LABELS[reason];
  if (reason.startsWith('rate_limit_')) {
    const scope = reason.slice('rate_limit_'.length);
    return RATE_LIMIT_SCOPE_LABELS[scope] ?? 'Too many submissions';
  }
  const words = reason.replace(/[_-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Other';
}

/** Plain-English reason the welcome email was paused (breaker reason keys). */
export function welcomePausedLabel(reason: string): string {
  const known: Record<string, string> = {
    bounce_rate: 'too many welcome emails bounced',
    complaint_rate: 'too many recipients marked welcome emails as spam',
    complaints: 'several recipients marked welcome emails as spam',
  };
  return known[reason] ?? reason.replace(/[_-]+/g, ' ');
}
